import { triggerDownload } from "@/shared/lib/download";

/**
 * GEO 报告「导出 HTML」——所见即所得单文件导出。
 *
 * 做法:把报告容器 DOM clone 一份,连同当前页面同源样式表的 CSS 规则文本一起拼进一个
 * 独立 `<!doctype html>` 文档,再走 Blob → `<a download>` 触发浏览器保存。零后端改动,
 * 图表(SVG)、雷达图、原话回放全部是当前渲染出的真实 DOM,带走即所见。
 *
 * 与「导出 PDF」(window.print())的分工:PDF 走浏览器打印机制,排版由 `@media print`
 * 接管;HTML 导出是给微信/邮件直接发的单文件,不依赖收件人的浏览器打印设置。
 */

export interface ExportReportHtmlOptions {
  /** 品牌名,用于文件名与默认标题。 */
  brand: string;
  /** 导出文档的 <title>;缺省 `${brand} · GEO 品牌效果报告`。 */
  title?: string;
  /**
   * 报告页内「仅本页交互用」的元素选择器(导出/重新体检等按钮)——这些元素在导出副本里
   * 原样保留在 DOM 里(不删除,免得破坏布局),但通过注入的 CSS 强制隐藏。
   * 默认命中 `data-geo-export-hide` 标记(参考现有 `@media print` 隐藏交互控件的做法)。
   */
  hideSelector?: string;
  /** 覆盖“当前时间”,便于测试;缺省 `new Date()`。 */
  now?: Date;
}

const DEFAULT_HIDE_SELECTOR = "[data-geo-export-hide]";

/** 文件名不允许的字符统统去掉(含路径分隔符、Windows 保留字符、空白)。 */
function safeFilenamePart(raw: string): string {
  const cleaned = (raw || "").replace(/[\\/:*?"<>|\s]+/g, "");
  return cleaned || "报告";
}

function dateStamp(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}${m}${day}`;
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c
  );
}

/**
 * 收集一组样式表的 CSS 规则文本。逐表 try/catch —— 跨域样式表(如未来接入的外链字体 CDN)
 * 读 `.cssRules` 会抛 SecurityError,跳过它不影响其余样式收集(导出丢一份字体好过整体失败)。
 * 拆成独立函数是为了能直接喂假 sheet 做单测,不依赖 jsdom 对 `document.styleSheets` 的还原度。
 */
export function collectStyleText(sheets: Iterable<CSSStyleSheet>): string {
  const chunks: string[] = [];
  for (const sheet of sheets) {
    try {
      const rules = sheet.cssRules;
      if (!rules) continue;
      for (const rule of Array.from(rules)) chunks.push(rule.cssText);
    } catch {
      // 跨域样式表(CORS 未放行)——跳过,不让它拖垮整份导出。
      continue;
    }
  }
  return chunks.join("\n");
}

/**
 * 把报告容器导出成一份可离线打开的单文件 HTML,触发浏览器下载。
 *
 * @param container 报告根节点(clone 这个节点,不改动页面上的真实 DOM)。
 */
export function exportReportHtml(container: HTMLElement, options: ExportReportHtmlOptions): void {
  const { brand, title, hideSelector = DEFAULT_HIDE_SELECTOR, now = new Date() } = options;
  const doc = container.ownerDocument;
  const win = doc.defaultView;
  if (!win) return; // detached document,没有 window 就没法 createObjectURL

  const skin = doc.documentElement.getAttribute("data-skin") ?? "";
  const clone = container.cloneNode(true) as HTMLElement;
  const pageTitle = title || `${brand} · GEO 品牌效果报告`;
  const css = collectStyleText(Array.from(doc.styleSheets));

  const html = [
    "<!doctype html>",
    `<html data-skin="${escapeHtml(skin)}">`,
    "<head>",
    '<meta charset="utf-8">',
    `<title>${escapeHtml(pageTitle)}</title>`,
    "<style>",
    css,
    // 交互按钮(导出/设为基线等)只在报告页里有意义,导出副本里强制隐藏——
    // 不删节点是为了不破坏周围布局(参考 index.css 里 @media print 的同款做法)。
    `${hideSelector}{display:none!important}`,
    "</style>",
    "</head>",
    `<body class="${escapeHtml(doc.body.className)}">`,
    clone.outerHTML,
    "</body>",
    "</html>",
  ].join("\n");

  // 用全局 Blob(不是 win.Blob):真实浏览器里两者是同一个构造函数,
  // 但 jsdom 自带的 Blob polyfill 缺 `.text()`,全局 Blob(Node 内建)在单测里更好读回内容。
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = win.URL.createObjectURL(blob);
  const filename = `GEO报告-${safeFilenamePart(brand)}-${dateStamp(now)}.html`;
  triggerDownload(url, filename);
  win.setTimeout(() => win.URL.revokeObjectURL(url), 2000);
}
